import { useState, useEffect } from 'react';
import { Box, Divider, Alert, Card, CardContent } from '@mui/material';
import { Comment as CommentIcon, Edit, Delete, Save, Cancel } from '@mui/icons-material';
import { Comment, CommentsResponse, getAdComments, updateComment, deleteComment } from '../../businessAdsUtils';
import { Button, Input, IconButton, Loader, Typography, Dialog } from '@sm-dashboard/ui-components';

interface AdminCommentsSectionProps {
  adId: string;
}

const AdminCommentsSection = ({ adId }: AdminCommentsSectionProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async (pageNum = 0, reset = false) => {
    try {
      if (pageNum === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response: CommentsResponse = await getAdComments(adId, pageNum, 10);

      if (reset || pageNum === 0) {
        setComments(response.comments);
      } else {
        setComments(prev => [...prev, ...response.comments]);
      }

      setHasMore(response.pagination.hasMore);
      setPage(pageNum);
      setError(null);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('שגיאה בטעינת התגובות');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchComments(0, true);
  }, [adId]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchComments(page + 1);
    }
  };

  const handleEditClick = (comment: Comment) => {
    setEditingId(comment._id);
    setEditContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim() || submitting) return;

    setSubmitting(true);
    try {
      const updatedComment = await updateComment(commentId, editContent.trim());
      if (updatedComment) {
        setComments(prev => prev.map(c => (c._id === commentId ? updatedComment : c)));
        setEditingId(null);
        setEditContent('');
      } else {
        console.error('Updated comment is null or undefined');
        setError('שגיאה בעדכון התגובה');
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      setError('שגיאה בעדכון התגובה');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!commentToDelete || submitting) return;

    setSubmitting(true);
    try {
      const success = await deleteComment(commentToDelete);
      if (success) {
        setComments(prev => prev.filter(c => c._id !== commentToDelete));
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
      } else {
        setError('שגיאה במחיקת התגובה');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError('שגיאה במחיקת התגובה');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && comments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CommentIcon />
        <Typography variant="h3">תגובות ({comments.length})</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {comments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            אין תגובות למודעה זו
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {comments.map(comment => (
            <Card key={comment._id} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2">{comment.shaliachName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(comment.createdAt)}
                      {comment.updatedAt !== comment.createdAt && ' (עודכן)'}
                    </Typography>
                  </Box>
                  {editingId !== comment._id && (
                    <Box>
                      <IconButton
                        shape="circle"
                        variant="text"
                        color="primary"
                        size="medium"
                        aria-label="עריכת תגובה"
                        onClick={() => handleEditClick(comment)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        shape="circle"
                        color="error"
                        size="medium"
                        aria-label="מחק תגובה"
                        onClick={() => handleDeleteClick(comment._id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {editingId === comment._id ? (
                  <Box sx={{ mt: 2 }}>
                    <Input
                      appearance="plain"
                      multiline
                      rows={3}
                      value={editContent}
                      onChange={event => setEditContent(event.target.value ?? '')}
                      disabled={submitting}
                      inputProps={{ maxLength: 500 }}
                      helperText={`${editContent.length}/500 תווים`}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Save />}
                        onClick={() => handleSaveEdit(comment._id)}
                        disabled={!editContent.trim()}
                        loading={submitting}
                        fullWidth={false}
                      >
                        שמור
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        disabled={submitting}
                        fullWidth={false}
                      >
                        ביטול
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{comment.content}</Box>
                )}
              </CardContent>
            </Card>
          ))}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="outlined" onClick={handleLoadMore} loading={loadingMore} fullWidth={false}>
                טען עוד תגובות
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        title="מחיקת תגובה"
        cancelAction={{ disabled: submitting }}
        primaryAction={{ label: 'מחק', onClick: handleConfirmDelete, loading: submitting, color: 'error' }}
      >
        <Typography variant="body" color="text.secondary">
          האם אתה בטוח שברצונך למחוק תגובה זו? פעולה זו אינה ניתנת לביטול.
        </Typography>
      </Dialog>
    </Box>
  );
};

export default AdminCommentsSection;
